import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import { Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import PlainTextEditor from 'ui/components/PlainTextEditor'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import ButtonsEditor from '../components/ButtonsEditor'
import ConfirmDialog from '../components/ConfirmDialog'
import { highlightsApi } from '../api/highlightsApi'
import { extractApiErrorMessage } from '../api/apiErrors'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const CardContentEditor = () => {
  const { cardId } = useParams()
  const navigate = useNavigate()

  const [card, setCard] = useState(null)
  const [isNew, setIsNew] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const [body, setBody] = useState('')
  const [mediaUrl, setMediaUrl] = useState('')
  const [buttons, setButtons] = useState([])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [cardsResult, contentsResult] = await Promise.all([
        highlightsApi.listCards(),
        highlightsApi.listAllContent(),
      ])
      const cards = cardsResult?.data?.cards || []
      const found = cards.find((c) => c.id === cardId)
      setCard(found || null)

      const contents = contentsResult?.data?.content || []
      const existing = contents.find((c) => c.card_id === cardId)
      if (existing) {
        setBody(existing.body || '')
        setMediaUrl(existing.media_url || '')
        setButtons(existing.buttons || [])
        setIsNew(false)
      } else {
        setIsNew(true)
      }
    } catch (err) {
      setError('Failed to load data. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [cardId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSave = useCallback(async () => {
    setError(null)
    setSaving(true)
    try {
      const data = { body, media_url: mediaUrl, buttons }
      if (isNew) {
        await highlightsApi.createContent({ card_id: cardId, ...data })
      } else {
        await highlightsApi.updateContent(cardId, data)
      }
      navigate(`${ROUTE_METADATA.adminOutreachHighlightCards.path}/${cardId}`)
    } catch (err) {
      setError(`Failed to save content: ${extractApiErrorMessage(err)}`)
    } finally {
      setSaving(false)
    }
  }, [body, mediaUrl, buttons, isNew, cardId, navigate])

  const handleDelete = useCallback(async () => {
    setDeleting(true)
    try {
      await highlightsApi.deleteContent(cardId)
      navigate(`${ROUTE_METADATA.adminOutreachHighlightCards.path}/${cardId}`)
    } catch (err) {
      setError(`Failed to delete content: ${extractApiErrorMessage(err)}`)
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }, [cardId, navigate])

  if (loading) return <Loading />

  if (!card) {
    return <Alert severity="error">Highlight card not found</Alert>
  }

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          {isNew ? 'New Card Content' : 'Edit Card Content'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `${ROUTE_METADATA.adminOutreachHighlightCards.path}/${cardId}`,
              )
            }
          >
            Back to Card
          </Button>
          {!isNew && (
            <Button
              type="secondary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ color: 'error.main' }}
            >
              Delete Content
            </Button>
          )}
        </Box>
      </AdminPageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ flex: 1 }}>
        <Grid item xs={12} md={7}>
          <Card elevation={0}>
            <CardContent
              sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Body
                </Typography>
                <PlainTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Main content text — use bold and bullets"
                  style={{ minHeight: 200 }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Media URL
                </Typography>
                <MuiTextField
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="https://..."
                  size="small"
                  fullWidth
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Buttons
                </Typography>
                <ButtonsEditor value={buttons} onChange={setButtons} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Card
            elevation={0}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <CardContent sx={{ p: 3, flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Content Preview
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  lineHeight: 1.6,
                  minHeight: 60,
                }}
              >
                {body || (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.disabled"
                  >
                    No body content yet
                  </Typography>
                )}
              </Typography>
              {buttons.length > 0 && (
                <Box
                  sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Buttons
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {buttons.map((btn) => (
                      <Box
                        key={`${btn.title}-${btn.action}`}
                        sx={{
                          px: 2,
                          py: 0.75,
                          border: 1,
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          color: 'primary.main',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                        }}
                      >
                        {btn.title || 'Untitled'}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 4,
          pt: 3,
          pb: 8,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          type="secondary"
          onClick={() =>
            navigate(
              `${ROUTE_METADATA.adminOutreachHighlightCards.path}/${cardId}`,
            )
          }
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : isNew ? 'Create Content' : 'Update Content'}
        </Button>
      </Box>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Content"
        message="Are you sure? This will delete the content for this card."
        confirmLabel="Delete"
        loading={deleting}
      />
    </>
  )
}

export default CardContentEditor
